import React, {
  FormEvent,
  useContext,
} from 'react';
import {
  Description,
  Field,
  Input,
  Label, Select,
  Textarea,
} from '@headlessui/react';
import { useGetFormQuery, useSubmitFormieMutation } from '../../redux-modules/service';
import { Form as FormType, MandatoryVariant } from '../../types/components';
import SessionContext from '../../contexts/SessionContext';
import PhoneField from './PhoneField';

export default function Form({
  siteId,
  selectedVariant,
  selectedMandatoryVariant,
  formHandle,
}: {
  siteId: number,
  selectedVariant: number,
  selectedMandatoryVariant: MandatoryVariant | null,
  formHandle: string,
}) {
  const { data, isLoading, isFetching }: {
    data?: FormType,
    isLoading: boolean,
    isFetching: boolean,
  } = useGetFormQuery(formHandle);

  const [handleSubmit, { data: response }] = useSubmitFormieMutation();

  const session = useContext(SessionContext);
  const { csrfTokenName, csrfTokenValue } = session;

  if (data === undefined || isLoading || isFetching) {
    return null;
  }

  const {
    recaptchaSiteKey,
    recaptchaNotice,
    honeypotInputName,
    submitButtonLabel,
    handle,
    rows,
  } = data;

  return (
    <>
      {response && !response.success && (
        // eslint-disable-next-line react/no-danger
        <div className="rounded-md text-white bg-accent p-4 mb-4 sm:mb-6" dangerouslySetInnerHTML={{ __html: response.errorMessage }} />
      )}
      {response && response.success ? (
        // eslint-disable-next-line react/no-danger
        <div className="rounded-md text-green-800 bg-green-50 p-4 mb-4 sm:mb-6" dangerouslySetInnerHTML={{ __html: response.submitActionMessage }} />
      ) : (
        <form
          method="post"
          onSubmit={async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);

            // @ts-ignore
            await grecaptcha.enterprise.ready(async () => {
              // @ts-ignore
              const token = await grecaptcha.enterprise.execute(recaptchaSiteKey, {
                action: `formie_${handle}`,
              });
              formData.set('g-recaptcha-response', token);
              const result = await handleSubmit(formData);

              if (result.data?.success) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  event: 'formSubmission',
                  formId: handle,
                });
              }
            });
          }}
          noValidate
        >
          <input type="hidden" name="handle" value={handle} />
          <input type="hidden" name="siteId" value={siteId} />
          {csrfTokenName && csrfTokenValue && (
            <input type="hidden" name={csrfTokenName} value={csrfTokenValue} />
          )}
          {honeypotInputName && (
            <input type="hidden" name={honeypotInputName} value="" />
          )}
          {Object.entries(rows).map(([uid, fields]) => (
            <div className="mb-4 sm:mb-6 flex flex-row gap-x-2" key={uid}>
              {Object.values(fields).map((field) => {
                if (field.fieldType === 'hidden' && field.handle === 'product') {
                  return <input type="hidden" name={`fields[${field.handle}][]`} value={selectedVariant} key={field.handle} />;
                }
                if (field.fieldType === 'hidden' && field.handle === 'mandatoryProduct') {
                  if (selectedMandatoryVariant) {
                    return <input type="hidden" name={`fields[${field.handle}][]`} value={selectedMandatoryVariant.id} key={field.handle} />;
                  }
                  return '';
                }
                if (field.fieldType === 'textarea') {
                  return (
                    <Field className="w-full" key={field.handle}>
                      <Label className="mb-2">
                        {field.label}
                        {field.required && (
                          <span className="ml-1 text-accent">*</span>
                        )}
                      </Label>
                      <Textarea
                        name={`fields[${field.handle}]`}
                        placeholder={field.placeholder}
                        required={field.required}
                        className={response && Object.prototype.hasOwnProperty.call(response.errors, field.handle) ? '!border-accent' : ''}
                      />
                      {response
                        && Object.prototype.hasOwnProperty.call(response.errors, field.handle) && (
                          <Description className="text-accent">{response.errors[field.handle][0]}</Description>
                      )}
                    </Field>
                  );
                }
                if (field.fieldType === 'tel') {
                  return (
                    <PhoneField
                      field={field}
                      response={response}
                      key={field.handle}
                    />
                  );
                }
                if (field.fieldType === 'checkbox') {
                  return (
                    <div className="checkbox-holder mb-4 last:mb-0" key={field.handle}>
                      <label htmlFor={field.handle}>
                        {field.description && (
                          // eslint-disable-next-line react/no-danger
                          <span dangerouslySetInnerHTML={{ __html: field.description }} />
                        )}
                        <input
                          type="checkbox"
                          name={`fields[${field.handle}]`}
                          id={field.handle}
                          required={field.required}
                          value="1"
                        />
                      </label>
                      {response
                        && Object.prototype.hasOwnProperty.call(response.errors, field.handle) && (
                          <Description className="text-accent">{response.errors[field.handle][0]}</Description>
                      )}
                    </div>
                  );
                }
                if (field.fieldType === 'dropdown' && field.options) {
                  return (
                    <Field className="w-full" key={field.handle}>
                      <Label className="mb-2">
                        {field.label}
                        {field.required && (
                          <span className="ml-1 text-accent">*</span>
                        )}
                      </Label>
                      <Select name={`fields[${field.handle}]`}>
                        {Object.values(field.options).map((option) => (
                          <option
                            value={option.value}
                            key={option.value}
                            selected={option.isDefault}
                          >
                            {option.label}
                          </option>
                        ))}
                      </Select>
                      {response
                        && Object.prototype.hasOwnProperty.call(response.errors, field.handle) && (
                          <Description className="text-accent">{response.errors[field.handle][0]}</Description>
                      )}
                    </Field>
                  );
                }
                return (
                  <Field className="w-full" key={field.handle}>
                    <Label className="mb-2">
                      {field.label}
                      {field.required && (
                        <span className="ml-1 text-accent">*</span>
                      )}
                    </Label>
                    <Input
                      type={field.fieldType}
                      name={`fields[${field.handle}]`}
                      placeholder={field.placeholder}
                      required={field.required}
                      className={response && Object.prototype.hasOwnProperty.call(response.errors, field.handle) ? '!border-accent' : ''}
                    />
                    {response
                      && Object.prototype.hasOwnProperty.call(response.errors, field.handle) && (
                        <Description className="text-accent">{response.errors[field.handle][0]}</Description>
                    )}
                  </Field>
                );
              })}
            </div>
          ))}
          {recaptchaNotice && (
            /* eslint-disable-next-line react/no-danger */
            <p className="text-xs !mb-4 sm:!mb-6" dangerouslySetInnerHTML={{ __html: recaptchaNotice }} />
          )}
          <button className="btn btn-secondary btn-lg w-full" type="submit">{submitButtonLabel}</button>
        </form>
      )}
    </>
  );
}
